import React__default, { forwardRef, useRef, useState, useEffect } from 'react';
import { AkChevronDownIcon } from '@fuegokit/fuegoicons-react';
import InnerVisualSlot from './InnerVisualSlot.js';
import SelectClearButton from './SelectClearButton.js';
import { StyledSelectText, AtlasSelectItem } from './AtlasSelectItem.js';
import { StyledSelect, StyledSelectLabel, StyledSelectInputWrapper, UnstyledSelectInput, StyledSelectTrigger, StyledSelectPopover, StyledSelectListbox } from './StyledSelect.js';
import { useRefObjectAsForwardedRef } from '../../hooks/useRefObjectAsForwardedRef.js';
import Box from '../Box/Box.js';
import Text from '../Text/Text.js';

const generateUniqueId = () => {
  const array = new Uint32Array(2);
  crypto.getRandomValues(array);
  return `${array[0]}-${array[1]}`;
};
const Select = ({
  appearance = 'default',
  children,
  defaultItems,
  defaultValue,
  description,
  disabledItems,
  errorMessage,
  isInvalid,
  isClearable,
  isDisabled,
  isLoading: isLoadingProp = false,
  isTrailingVisualAnimated = true,
  items,
  label,
  loaderPosition = 'trailing',
  onBlur,
  onFocus,
  onSelect,
  onChange,
  onOpenChange,
  placeholder,
  selectedKey,
  testId,
  trailingAction,
  trailingVisual: TrailingVisual = AkChevronDownIcon,
  leadingVisual: LeadingVisual,
  value,
  ...props
}, forwardedRef) => {
  const inputWrapperRef = useRef(null);
  const inputRef = useRef(null);
  const popoverRef = useRef(null);
  const [isInputFocused, setIsInputFocused] = useState(false);
  // local state to synchronize with the isLoading prop
  const localLoadingState = isLoadingProp;
  const [isLoading, setIsLoading] = useState(false);

  // Synchronize forwardedRef with the input ref
  useRefObjectAsForwardedRef(forwardedRef, inputRef);

  // Sync internal loading state with prop
  useEffect(() => {
    setIsLoading(localLoadingState);
  }, [localLoadingState]);
  const showLeadingLoadingIndicator = isLoading && (loaderPosition === 'leading' || Boolean(LeadingVisual && loaderPosition !== 'trailing'));
  const showTrailingLoadingIndicator = isLoading && loaderPosition === 'trailing';
  const handleOnFocus = event => {
    setIsInputFocused(true);
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    onFocus && onFocus(event);
  };
  const handleOnBlur = event => {
    setIsInputFocused(false);
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    onBlur && onBlur(event);
  };
  const handleSelectionChange = key => {
    setIsInputFocused(false); // Remove input focus after selection
    if (onSelect) {
      onSelect(key);
    }
  };
  const handleInputChange = event => {
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    onChange && onChange(event);
  };
  // Show the entire list if the user opens the select menu manually
  const handleOpenChange = (isOpen, menuTrigger) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    onOpenChange && onOpenChange(isOpen, menuTrigger);
  };
  return /*#__PURE__*/React__default.createElement(StyledSelect, {
    menuTrigger: "focus",
    defaultItems: items,
    onSelectionChange: handleSelectionChange,
    isDisabled: isDisabled,
    testId: testId ? `${testId}-select-root` : undefined,
    disabledKeys: disabledItems,
    selectedKey: isLoading && loaderPosition === 'leading' ? '' : selectedKey,
    onOpenChange: handleOpenChange,
    inputValue: value
  }, /*#__PURE__*/React__default.createElement(StyledSelectLabel, {
    "data-testid": testId ? `${testId}-select-label` : undefined
  }, label), /*#__PURE__*/React__default.createElement(Box, {
    sx: {
      position: 'relative'
    }
  }, /*#__PURE__*/React__default.createElement(StyledSelectInputWrapper, {
    appearance: appearance,
    isInputFocused: isInputFocused,
    isDisabled: isDisabled,
    isInvalid: isInvalid,
    "data-testid": testId ? `${testId}-select-input-wrapper` : undefined,
    ref: inputWrapperRef
  }, /*#__PURE__*/React__default.createElement(Box, {
    sx: {
      position: 'absolute',
      left: 0,
      top: 0,
      p: 2
    }
  }, (showLeadingLoadingIndicator || LeadingVisual) && /*#__PURE__*/React__default.createElement(InnerVisualSlot, {
    visualPosition: "leading",
    showLoadingIndicator: showLeadingLoadingIndicator,
    hasLoadingIndicator: typeof isLoading === 'boolean'
  }, LeadingVisual && typeof LeadingVisual === 'function' ? /*#__PURE__*/React__default.createElement(LeadingVisual, null) : LeadingVisual)), /*#__PURE__*/React__default.createElement(UnstyledSelectInput, {
    placeholder: showLeadingLoadingIndicator ? '' : placeholder,
    ref: inputRef,
    disabled: isLoading || isDisabled,
    onFocus: handleOnFocus,
    onBlur: handleOnBlur,
    onChange: handleInputChange,
    testId: testId ? `${testId}-unstyled-input` : undefined
  }), /*#__PURE__*/React__default.createElement(Box, {
    sx: {
      position: 'absolute',
      right: 0,
      top: 0,
      p: 2
    }
  }, (showTrailingLoadingIndicator || TrailingVisual) && /*#__PURE__*/React__default.createElement(InnerVisualSlot, {
    visualPosition: "trailing",
    showLoadingIndicator: showTrailingLoadingIndicator,
    hasLoadingIndicator: typeof isLoading === 'boolean'
  }, /*#__PURE__*/React__default.createElement(StyledSelectTrigger, {
    isTrailingVisualAnimated: isTrailingVisualAnimated,
    "data-testid": testId ? `${testId}-select-button` : undefined,
    isDisabled: isLoading || isDisabled
  }, TrailingVisual && typeof TrailingVisual === 'function' ? /*#__PURE__*/React__default.createElement(TrailingVisual, null) : TrailingVisual || /*#__PURE__*/React__default.createElement(AkChevronDownIcon, null)))), /*#__PURE__*/React__default.createElement(Box, {
    sx: {
      position: 'absolute',
      right: '30px',
      top: '2px',
      p: 2
    }
  }, isClearable ? /*#__PURE__*/React__default.createElement(SelectClearButton, null) : trailingAction || null)), description && !isInvalid ? /*#__PURE__*/React__default.createElement(StyledSelectText, {
    slot: "description"
  }, description) : null, errorMessage && isInvalid ? /*#__PURE__*/React__default.createElement(StyledSelectText, {
    slot: "errorMessage"
  }, errorMessage) : null, /*#__PURE__*/React__default.createElement(StyledSelectPopover, {
    isNonModal: true,
    ref: popoverRef
  }, /*#__PURE__*/React__default.createElement(StyledSelectListbox, {
    "data-testid": testId ? `${testId}-select-listbox` : undefined
  }, item => typeof children === 'function' ? children(item) : /*#__PURE__*/React__default.createElement(AtlasSelectItem, {
    key: testId ? `test-uuid-${item.name}` : generateUniqueId(),
    textValue: item.label,
    label: item.label,
    leadingVisual: item.icon,
    "data-testid": testId ? `${testId}-select-item` : undefined,
    id: item.id ? item.id : generateUniqueId()
  }, /*#__PURE__*/React__default.createElement(Box, {
    sx: {
      display: 'flex',
      gap: 1
    }
  }, /*#__PURE__*/React__default.createElement(Text, null, item.label)))))));
};
Select.displayName = "Select";
var AtlasSelect = /*#__PURE__*/forwardRef(Select);
Select.displayName = 'Select';

export { AtlasSelect as default };
